function slugifyLabel(label) {
  return String(label)
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function normalizeComparableText(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export function makeOptions(labels = []) {
  return labels.map((label) => ({
    value: slugifyLabel(label),
    label,
  }));
}

export function labelForValue(options = [], value = '') {
  if (value === null || value === undefined) return '';
  const text = String(value).trim();
  if (!text) return '';
  const normalized = normalizeComparableText(text);

  const match = options.find((option) => {
    const candidates = [option?.value, option?.label, option?.name]
      .filter(Boolean)
      .map(normalizeComparableText);
    return candidates.includes(normalized);
  });

  return match ? String(match.label ?? match.value ?? text) : text;
}

export const MARKET_TYPE_OPTIONS = [
  { value: 'live_animal',        label: 'Live Animal' },
  { value: 'abattoir_market',    label: 'Abattoir Market' },
  { value: 'processing_market',  label: 'Processing Market' },
  { value: 'input_feed',         label: 'Input / Feed' },
  { value: 'mixed',              label: 'Mixed' },
  { value: 'border_market',      label: 'Border Market' },
  { value: 'terminal_market',    label: 'Terminal Market' },
];

export const MARKET_STATUS_OPTIONS = [
  { value: 'active',               label: 'Active' },
  { value: 'inactive',             label: 'Inactive' },
  { value: 'seasonal',             label: 'Seasonal' },
  { value: 'under_rehabilitation', label: 'Under Rehabilitation' },
];

// Universal workflow pipeline — Grey/Blue/Amber/Green/Red  (no exceptions per spec)
export const WORKFLOW_STATUS_STYLE = {
  draft:     { bg: '#f5f5f4', color: '#888780', border: '#d6d3d1' },
  submitted: { bg: '#eff6ff', color: '#185FA5', border: '#bfdbfe' },
  validated: { bg: '#fffbeb', color: '#BA7517', border: '#fde68a' },
  approved:  { bg: '#f0fdf4', color: '#3B6D11', border: '#bbf7d0' },
  rejected:  { bg: '#fff1f2', color: '#A32D2D', border: '#fecdd3' },
};

// Market Registry operational status — RAG coded
export const MARKET_OPERATIONAL_STATUS_STYLE = {
  active:               { bg: '#f0fdf4', color: '#3B6D11', border: '#bbf7d0' },
  inactive:             { bg: '#fff1f2', color: '#A32D2D', border: '#fecdd3' },
  seasonal:             { bg: '#fffbeb', color: '#BA7517', border: '#fde68a' },
  under_rehabilitation: { bg: '#eff6ff', color: '#185FA5', border: '#bfdbfe' },
};

export const MARKET_SCHEDULE_OPTIONS = makeOptions([
  'Daily',
  'Weekly',
  'Bi-Weekly',
  'Monthly',
  'Periodic',
  'As Needed',
]);

export const PRICE_TYPE_OPTIONS = makeOptions([
  'farm_gate',
  'wholesale',
  'retail',
  'auction',
]);

export const COMMODITY_FORM_OPTIONS = makeOptions([
  'live_animal',
  'carcass_whole',
  'quarters',
  'cut',
  'processed',
  'by_product',
]);

export const PRICE_UNIT_OPTIONS = makeOptions([
  'per_head',
  'per_kg_liveweight',
  'per_kg_carcass',
  'per_crate_30',
  'per_bag_50kg',
  'per_litre',
]);

export const SEASONAL_FLAG_OPTIONS = [
  { value: 'regular', label: 'Regular' },
  { value: 'festival_peak_sallah', label: 'Festival Peak Sallah' },
  { value: 'festival_peak_christmas', label: 'Festival Peak Christmas' },
  { value: 'seasonal_low', label: 'Seasonal Low' },
  { value: 'other', label: 'Other' },
];

const LEGACY_SEASONAL_FLAG_MAP = {
  peak: 'festival_peak_sallah',
  normal: 'regular',
  off_peak: 'seasonal_low',
  offpeak: 'seasonal_low',
  festive: 'festival_peak_christmas',
  lean: 'seasonal_low',
  yes: 'regular',
  no: 'seasonal_low',
  true: 'regular',
  false: 'seasonal_low',
};

export function normalizeSeasonalFlagValue(value) {
  if (value === null || value === undefined) return '';
  if (value === true) return 'regular';
  if (value === false) return 'seasonal_low';

  const text = String(value).trim();
  if (!text) return '';

  const normalized = text.toLowerCase().replace(/[\s-]+/g, '_');
  return LEGACY_SEASONAL_FLAG_MAP[normalized] ?? text;
}

export function seasonalFlagLabel(value) {
  const normalized = normalizeSeasonalFlagValue(value);
  const match = SEASONAL_FLAG_OPTIONS.find((option) => option.value === normalized);
  return match?.label ?? String(value ?? '').trim();
}

export const GRADE_QUALITY_OPTIONS = makeOptions([
  'grade_1',
  'grade_2',
  'grade_3',
  'ungraded',
]);

export const BORDER_DIRECTION_OPTIONS = [
  { value: 'import',  label: 'Import' },
  { value: 'export',  label: 'Export' },
  { value: 'transit', label: 'Transit' },
];

export const INSPECTION_STATUS_OPTIONS = makeOptions([
  'cleared',
  'held',
  'rejected',
]);

export const BORDER_POINT_OPTIONS = [
  { value: 'maigatari', label: 'Maigatari' },
  { value: 'illela', label: 'Illela' },
  { value: 'seme', label: 'Seme' },
  { value: 'idiroko', label: 'Idiroko' },
  { value: 'mfum', label: 'Mfum' },
];
